import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { format, parseISO } from "date-fns";
import {
  Plus,
  Eye,
  SearchX,
  Building2,
  ChevronLeft,
  ChevronRight,
  Clock,
  CheckCircle2,
} from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";

const PER_PAGE = 25;

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return format(parseISO(iso), "dd MMM yyyy");
  } catch {
    return iso;
  }
};

export default function CustomersList() {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, per_page: PER_PAGE };
      if (query) params.q = query;
      if (statusFilter !== "all") params.status = statusFilter;
      const { data } = await api.get("/customers", { params });
      setItems(data.items || []);
      setTotal(data.total || 0);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load customers");
    } finally {
      setLoading(false);
    }
  }, [page, query, statusFilter]);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [load]);

  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const canCreate = user?.role !== "billing";

  return (
    <div className="grain ui-sans pb-16" data-testid="customers-list-page">
      <main className="max-w-6xl mx-auto px-4 sm:px-6 mt-8 space-y-6">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="brand-serif text-3xl sm:text-4xl font-bold text-[#1d3a14]">
              Customer Master
            </h1>
            <p className="text-sm text-[#5b6b4a] mt-1">
              {total} customer{total !== 1 ? "s" : ""}
              {user?.role === "salesperson" && " assigned to you"}
            </p>
          </div>
          {canCreate && (
            <Link to="/customers/new" data-testid="new-customer-link">
              <Button className="bg-[#1d3a14] hover:bg-[#264a1a] text-[#faf6ea]">
                <Plus className="h-4 w-4 mr-2" />
                New Customer
              </Button>
            </Link>
          )}
        </div>

        <div className="bg-white/80 border border-[#d6cdb4] rounded-xl p-4 flex gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px]">
            <Input
              placeholder="Search company, mobile, email, city, GSTIN, segment, salesperson…"
              value={query}
              onChange={(e) => {
                setPage(1);
                setQuery(e.target.value);
              }}
              data-testid="customers-search-input"
            />
          </div>
          <Select
            value={statusFilter}
            onValueChange={(v) => {
              setPage(1);
              setStatusFilter(v);
            }}
          >
            <SelectTrigger
              className="w-48"
              data-testid="customers-status-filter"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all" data-testid="cust-status-all">
                All statuses
              </SelectItem>
              <SelectItem value="pending" data-testid="cust-status-pending">
                Pending
              </SelectItem>
              <SelectItem value="approved" data-testid="cust-status-approved">
                Approved
              </SelectItem>
            </SelectContent>
          </Select>
        </div>

        {loading ? (
          <p
            className="text-center text-[#5b6b4a] py-12"
            data-testid="customers-loading"
          >
            Loading…
          </p>
        ) : items.length === 0 ? (
          <div
            className="bg-white/80 border border-dashed border-[#c4b991] rounded-xl p-12 text-center"
            data-testid="customers-empty"
          >
            <SearchX className="h-10 w-10 text-[#5b6b4a] mx-auto mb-3" />
            <p className="text-[#5b6b4a]">
              No customers match the current search.
            </p>
            {canCreate && (
              <Link to="/customers/new" className="inline-block mt-4">
                <Button
                  variant="outline"
                  className="border-[#c4b991] hover:bg-[#efe7cf]"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Add your first customer
                </Button>
              </Link>
            )}
          </div>
        ) : (
          <>
            <div className="overflow-x-auto bg-white/85 border border-[#d6cdb4] rounded-xl shadow-sm">
              <table
                className="w-full text-sm"
                data-testid="customers-table"
              >
                <thead className="bg-[#1d3a14] text-[#faf6ea]">
                  <tr>
                    <th className="text-left px-3 py-2">Company</th>
                    <th className="text-left px-3 py-2">Mobile</th>
                    <th className="text-left px-3 py-2">City / State</th>
                    <th className="text-left px-3 py-2">GSTIN</th>
                    <th className="text-left px-3 py-2">Segment</th>
                    <th className="text-left px-3 py-2">Salesperson</th>
                    <th className="text-left px-3 py-2">Status</th>
                    <th className="text-left px-3 py-2">Added</th>
                    <th className="text-right px-3 py-2">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((c, i) => (
                    <tr
                      key={c.customer_id}
                      className={cn(
                        "border-t border-[#efe7cf]",
                        i % 2 === 0 ? "bg-[#fbf8ec]" : "bg-white"
                      )}
                      data-testid={`customer-row-${c.customer_id}`}
                    >
                      <td className="px-3 py-2">
                        <div className="flex items-start gap-2">
                          <Building2 className="h-4 w-4 text-[#264a1a] mt-0.5 shrink-0" />
                          <div>
                            <div className="font-medium text-[#1d3a14]">
                              {c.company_name}
                            </div>
                            {c.email && (
                              <div className="text-xs text-[#5b6b4a]">
                                {c.email}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2 font-mono text-[13px]">
                        {c.mobile}
                      </td>
                      <td className="px-3 py-2">
                        <div>{c.city || "—"}</div>
                        {c.state && (
                          <div className="text-xs text-[#5b6b4a]">{c.state}</div>
                        )}
                      </td>
                      <td className="px-3 py-2 font-mono text-xs">
                        {c.gstin || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {c.segment || "—"}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#5b6b4a]">
                        {c.assigned_salesperson_email || "—"}
                      </td>
                      <td className="px-3 py-2">
                        {c.status === "pending" ? (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border bg-[#c47b28]/15 text-[#8a4c0d] border-[#c47b28]/40"
                            data-testid={`cust-status-badge-${c.customer_id}`}
                          >
                            <Clock className="h-3 w-3" />
                            Pending
                          </span>
                        ) : (
                          <span
                            className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full border bg-[#1d3a14]/15 text-[#1d3a14] border-[#1d3a14]/40"
                            data-testid={`cust-status-badge-${c.customer_id}`}
                          >
                            <CheckCircle2 className="h-3 w-3" />
                            Approved
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-xs text-[#5b6b4a]">
                        {formatDate(c.created_at)}
                      </td>
                      <td className="px-3 py-2 text-right">
                        <Link
                          to={`/customers/${c.customer_id}`}
                          data-testid={`view-customer-${c.customer_id}`}
                        >
                          <Button
                            size="sm"
                            variant="outline"
                            className="border-[#c4b991] hover:bg-[#efe7cf]"
                          >
                            <Eye className="h-3.5 w-3.5 mr-1" />
                            View
                          </Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3 text-sm text-[#5b6b4a]">
              <div data-testid="customers-pagination-info">
                Showing {(page - 1) * PER_PAGE + 1}–
                {Math.min(page * PER_PAGE, total)} of {total}
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page <= 1}
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  data-testid="customers-prev-page"
                  className="border-[#c4b991] hover:bg-[#efe7cf]"
                >
                  <ChevronLeft className="h-4 w-4" />
                  Prev
                </Button>
                <span data-testid="customers-page-indicator">
                  Page {page} / {totalPages}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={page >= totalPages}
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  data-testid="customers-next-page"
                  className="border-[#c4b991] hover:bg-[#efe7cf]"
                >
                  Next
                  <ChevronRight className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
